import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, User, Briefcase, Receipt, Loader2, X, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { API } from "../../App";
import { useTheme } from "../../contexts/ThemeContext";

const STATUS_STYLES = {
  scheduled: "bg-blue-500/15 text-blue-500",
  in_progress: "bg-amber-500/15 text-amber-500",
  deposit_collected: "bg-teal-500/15 text-teal-500",
  completed: "bg-emerald-500/15 text-emerald-500",
  completed_pending_payment: "bg-yellow-500/15 text-yellow-600",
  cancelled: "bg-red-500/15 text-red-500",
  pending: "bg-gray-500/15 text-gray-500",
  paid: "bg-emerald-500/15 text-emerald-500",
  sent: "bg-blue-500/15 text-blue-500",
  draft: "bg-gray-500/15 text-gray-500",
  overdue: "bg-red-500/15 text-red-500",
  partially_paid: "bg-amber-500/15 text-amber-500",
};

const statusLabel = (s) => (s || "").replace(/_/g, " ");

// Recent searches — persisted per logged-in user in localStorage.
const RECENTS_MAX = 6;
const recentsKey = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return `recent_searches_${u.user_id || u.id || u.email || "anon"}`;
  } catch {
    return "recent_searches_anon";
  }
};
const loadRecents = () => {
  try {
    const v = JSON.parse(localStorage.getItem(recentsKey()) || "[]");
    return Array.isArray(v) ? v.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
};

// Compose a one-line address from whatever parts exist (street, city, zip).
const fmtAddress = (...parts) =>
  parts.filter((p) => p && String(p).trim() && String(p).toLowerCase() !== "none").join(", ");

// Address + ZIP without duplicating a ZIP already embedded in the string.
const addrWithZip = (addr, zip) => {
  const a = (addr || "").trim();
  const z = (zip || "").trim();
  if (!a) return z;
  if (!z || a.includes(z)) return a;
  return `${a}, ${z}`;
};

// Header global search — finds customers, jobs and invoices/estimates with
// grouped suggestions. Desktop: inline input. Mobile: icon → full-width bar.
export const GlobalSearch = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [recents, setRecents] = useState(loadRecents);
  const [showRecents, setShowRecents] = useState(false);
  const seqRef = useRef(0);
  const boxRef = useRef(null);
  const mobileRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) { setResults(null); setOpen(false); return; }
    const t = setTimeout(async () => {
      const seq = ++seqRef.current;
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/search/global?q=${encodeURIComponent(query.trim())}&limit=8`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (seq === seqRef.current) { setResults(res.data); setOpen(true); setActiveIdx(0); }
      } catch {
        if (seq === seqRef.current) setResults(null);
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current?.contains(e.target) || mobileRef.current?.contains(e.target)) return;
      setOpen(false);
      setMobileOpen(false);
      setShowRecents(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Cmd/Ctrl+K focuses the search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (window.innerWidth < 768) setMobileOpen(true);
        else inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const flat = [
    ...(results?.customers || []).map((r) => ({ ...r, _type: "customer" })),
    ...(results?.jobs || []).map((r) => ({ ...r, _type: "job" })),
    ...(results?.invoices || []).map((r) => ({ ...r, _type: "invoice" })),
  ];
  const hasAny = flat.length > 0;

  const persistRecents = (list) => {
    setRecents(list);
    try { localStorage.setItem(recentsKey(), JSON.stringify(list)); } catch { /* full/blocked */ }
  };
  const saveRecent = (q) => {
    const t = (q || "").trim();
    if (t.length < 2) return;
    persistRecents([t, ...recents.filter((r) => r.toLowerCase() !== t.toLowerCase())].slice(0, RECENTS_MAX));
  };
  const removeRecent = (q) => persistRecents(recents.filter((r) => r !== q));

  const handleSelect = (item) => {
    saveRecent(query);
    setOpen(false);
    setMobileOpen(false);
    setShowRecents(false);
    setQuery("");
    setResults(null);
    if (item._type === "customer") navigate(`/dashboard/customers?open=${item.customer_id}`);
    else if (item._type === "job") navigate(`/dashboard/jobs?job=${item.job_id}`);
    else navigate(`/dashboard/invoices?open=${item.invoice_id}`);
  };

  const onKeyDown = (e) => {
    if (!open || !hasAny) {
      if (e.key === "Escape") { setOpen(false); setMobileOpen(false); setShowRecents(false); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (flat[activeIdx]) handleSelect(flat[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); setMobileOpen(false); }
  };

  const renderInput = (autoFocus = false) => (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <input
        ref={autoFocus ? undefined : inputRef}
        autoFocus={autoFocus}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => { setShowRecents(true); if (hasAny && query.trim().length >= 2) setOpen(true); }}
        onClick={() => setShowRecents(true)}
        onKeyDown={onKeyDown}
        placeholder="Search jobs, customers, invoices…"
        className={`w-full h-9 pl-9 pr-8 rounded-lg text-sm outline-none transition-colors border ${
          isDark
            ? "bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#FFCC00]/50"
            : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#0046BE]/60 focus:bg-white"
        }`}
        data-testid="global-search-input"
      />
      {loading ? (
        <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
      ) : query ? (
        <button
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          onClick={() => { setQuery(""); setResults(null); setOpen(false); }}
          data-testid="global-search-clear"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      ) : null}
    </div>
  );

  const Group = ({ label, icon: Icon, items, testid, children }) =>
    items.length > 0 && (
      <div data-testid={testid}>
        <p className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Icon className="w-3 h-3" /> {label}
        </p>
        {children}
      </div>
    );

  const Row = ({ item, idx, primary, secondary, secondaryLines, badge, badgeClass }) => (
    <button
      key={idx}
      onClick={() => handleSelect(item)}
      onMouseEnter={() => setActiveIdx(idx)}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left transition-colors ${
        idx === activeIdx ? (isDark ? "bg-white/10" : "bg-[#0046BE]/5") : ""
      }`}
      data-testid={`search-result-${item._type}-${item.customer_id || item.job_id || item.invoice_id}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground truncate">{primary}</p>
        {secondary && <p className="text-xs text-muted-foreground truncate">{secondary}</p>}
        {(secondaryLines || []).filter(Boolean).map((ln, i) => (
          <p key={i} className="text-xs text-muted-foreground whitespace-normal break-words leading-snug">{ln}</p>
        ))}
      </div>
      {badge && (
        <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded capitalize ${badgeClass || "bg-gray-500/15 text-gray-500"}`}>
          {badge}
        </span>
      )}
    </button>
  );

  // Recent-searches dropdown — shown on focus while the query is still empty.
  const renderRecents = () => (
    <div
      className={`absolute left-0 right-0 top-full mt-2 rounded-xl border shadow-xl overflow-hidden z-[60] ${
        isDark ? "bg-[#0B1220] border-white/10" : "bg-white border-gray-200"
      }`}
      data-testid="recent-searches-panel"
    >
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Recent searches
        </p>
        <button
          className="text-[10px] font-medium text-muted-foreground hover:text-foreground"
          onClick={() => persistRecents([])}
          data-testid="recent-searches-clear"
        >
          Clear
        </button>
      </div>
      {recents.map((r, i) => (
        <div
          key={r}
          className={`group flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
            isDark ? "hover:bg-white/10" : "hover:bg-[#0046BE]/5"
          }`}
          onClick={() => { setShowRecents(false); setQuery(r); inputRef.current?.focus(); }}
          data-testid={`recent-search-${i}`}
        >
          <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="flex-1 min-w-0 text-sm text-foreground truncate">{r}</span>
          <button
            className="shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground transition-opacity"
            onClick={(e) => { e.stopPropagation(); removeRecent(r); }}
            data-testid={`recent-search-remove-${i}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );

  const renderPanel = () => {
    let idx = -1;
    return (
      <div
        className={`absolute left-0 right-0 md:right-auto md:w-[440px] top-full mt-2 rounded-xl border shadow-xl overflow-hidden z-[60] max-h-[70vh] overflow-y-auto overscroll-contain touch-pan-y ${
          isDark ? "bg-[#0B1220] border-white/10" : "bg-white border-gray-200"
        }`}
        data-testid="global-search-results"
      >
        {!hasAny ? (
          <p className="px-3 py-4 text-sm text-muted-foreground text-center" data-testid="global-search-empty">
            No results for “{results?.query || query}”
          </p>
        ) : (
          <>
            <Group label="Customers" icon={User} items={results?.customers || []} testid="search-group-customers">
              {(results?.customers || []).map((c) => {
                idx += 1;
                return (
                  <Row
                    key={c.customer_id}
                    item={{ ...c, _type: "customer" }}
                    idx={idx}
                    primary={c.name}
                    secondary={[c.phone, c.secondary_phone].filter(Boolean).join(" · ")}
                    secondaryLines={[addrWithZip(c.display_address ?? fmtAddress(c.address, c.city), c.zip ?? c.zip_code)]}
                  />
                );
              })}
            </Group>
            <Group label="Jobs" icon={Briefcase} items={results?.jobs || []} testid="search-group-jobs">
              {(results?.jobs || []).map((j) => {
                idx += 1;
                return (
                  <Row
                    key={j.job_id}
                    item={{ ...j, _type: "job" }}
                    idx={idx}
                    primary={j.title || j.service_name || "Untitled job"}
                    secondary={[j.customer_name, j.job_display_id, (j.scheduled_date || "").slice(0, 10)].filter(Boolean).join(" · ")}
                    secondaryLines={[addrWithZip(j.display_address ?? fmtAddress(j.customer_address || j.address, j.address_city), j.zip ?? j.zip_code)]}
                    badge={statusLabel(j.status)}
                    badgeClass={STATUS_STYLES[j.status]}
                  />
                );
              })}
            </Group>
            <Group label="Invoices & Estimates" icon={Receipt} items={results?.invoices || []} testid="search-group-invoices">
              {(results?.invoices || []).map((inv) => {
                idx += 1;
                return (
                  <Row
                    key={inv.invoice_id}
                    item={{ ...inv, _type: "invoice" }}
                    idx={idx}
                    primary={`${inv.document_type === "estimate" ? "Estimate" : "Invoice"} #${inv.invoice_number || ""} — $${Number(inv.total || 0).toLocaleString()}`}
                    secondary={inv.customer_name}
                    badge={statusLabel(inv.status)}
                    badgeClass={STATUS_STYLES[inv.status]}
                  />
                );
              })}
            </Group>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Desktop inline search */}
      <div ref={boxRef} className="relative hidden md:block w-56 lg:w-80" data-testid="global-search">
        {renderInput()}
        {open && results && query.trim().length >= 2
          ? renderPanel()
          : showRecents && query.trim().length < 2 && recents.length > 0 && renderRecents()}
      </div>

      {/* Mobile: icon trigger + full-width bar under the header */}
      <Button
        variant="ghost"
        size="icon"
        className={`md:hidden ${isDark ? "text-white/60 hover:text-white hover:bg-white/5" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
        onClick={() => setMobileOpen(true)}
        data-testid="global-search-btn-mobile"
      >
        <Search className="w-5 h-5" />
      </Button>
      {mobileOpen && (
        <div
          ref={mobileRef}
          className={`fixed inset-x-0 top-16 z-[60] p-3 md:hidden border-b ${
            isDark ? "bg-[#020817] border-white/10" : "bg-white border-gray-200"
          }`}
          data-testid="global-search-mobile-bar"
        >
          <div className="relative">
            {renderInput(true)}
            {open && results && query.trim().length >= 2
              ? renderPanel()
              : showRecents && query.trim().length < 2 && recents.length > 0 && renderRecents()}
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
